import { fmtBrl, fmtInt } from '../shared/formatters'
import {
  asRevealed,
  buildPlaceholderRow,
  buildHybridAdditiveRow,
  buildHybridDisplayRow,
  buildMarkdownRow
} from '../rowBuilders'
import { buildDeltaPlanoRow, buildDeltaAnoAnteriorRow } from '../deltas'
import { cmv } from '../profitIndicators/cmv'
import { buildGrossProfitRow } from '../profitIndicators/grossMargin'
import { markup } from '../profitIndicators/markup'
import { isOtbEditableField } from '../strategy/otb.strategy'

export const buildSalesRows = ({
  monthKeys,
  summaryPeriods,
  planoAgg,
  realizadoAgg,
  hybridAgg,
  pecasAgg,
  reaisAgg,
  cmvAgg,
  realizadoLastYear,
  variacoes,
  forecast,
  custosPreco,
  usePriceCostAsCMV,
}) => {
  const faturamentoRows = [
    {
      key: "fat-base", label: "Histórico + Planejado", bold: true,
      editableField: isOtbEditableField('grossRevenue') ? 'grossRevenue' : undefined,
      ...buildHybridAdditiveRow({ monthKeys, hybridAgg, field: 'grossRevenue', fmt: fmtBrl, summaryPeriods }),
    },
  ];

  if (variacoes) {
    faturamentoRows.push(asRevealed({
      key: "fat-delta-plano", label: "Δ Plano",
      ...buildDeltaPlanoRow({ monthKeys, planoAgg, realizadoAgg, field: 'grossRevenue', summaryPeriods }),
    }));

    faturamentoRows.push(asRevealed({ key: "fat-delta-ano-anterior", label: "Δ Ano Anterior",
      ...buildDeltaAnoAnteriorRow({ monthKeys, realizadoAgg, realizadoLastYearData: realizadoLastYear, field: 'grossRevenue', summaryPeriods }),
    }));
  }

  if (forecast) {
    faturamentoRows.push(asRevealed({ key: "fat-forecast", label: "Forecast", forecastIcon: true, ...buildPlaceholderRow(monthKeys, summaryPeriods) }));
    faturamentoRows.push(asRevealed({ key: "fat-delta-forecast", label: "Δ Forecast", forecastIcon: true, ...buildPlaceholderRow(monthKeys, summaryPeriods) }));
  }

  const vendaPecasRows = [
    {
      key: "venda-base", label: "Histórico + Planejado",
      editableField: isOtbEditableField('unitsSold') ? 'unitsSold' : undefined,
      ...buildHybridAdditiveRow({ monthKeys, hybridAgg, field: 'unitsSold', fmt: fmtInt, summaryPeriods }),
    },
  ];

  if (variacoes) {
    vendaPecasRows.push(asRevealed({
      key: "venda-delta-plano", label: "Δ Plano",
      ...buildDeltaPlanoRow({ monthKeys, planoAgg, realizadoAgg, field: 'unitsSold', summaryPeriods }),
    }));
    vendaPecasRows.push(asRevealed({
      key: "venda-delta-ano-anterior",
      label: "Δ Ano Anterior",
      ...buildDeltaAnoAnteriorRow({
        monthKeys,
        realizadoAgg,
        realizadoLastYearData: realizadoLastYear,
        field: 'unitsSold',
        summaryPeriods
      }),
    }));
  }

  if (forecast) {
    vendaPecasRows.push(asRevealed({ key: "venda-forecast", label: "Forecast", forecastIcon: true, ...buildPlaceholderRow(monthKeys, summaryPeriods) }));
    vendaPecasRows.push(asRevealed({ key: "venda-delta-forecast", label: "Δ Forecast", forecastIcon: true, ...buildPlaceholderRow(monthKeys, summaryPeriods) }));
  }

  const precoRows = [
    {
      key: "preco-inicial", label: "PMV Inicial",
      editableField: isOtbEditableField('originalPrice') ? 'originalPrice' : undefined,
      ...buildHybridDisplayRow({ monthKeys, hybridAgg, field: 'originalPrice' }),
    },
  ];

  if (custosPreco) {
    precoRows.push(asRevealed({
      key: "preco-final", label: "PMV Final",
      ...buildHybridDisplayRow({ monthKeys, hybridAgg, field: 'salePrice' }),
    }));
    precoRows.push(asRevealed({
      key: "preco-markdown", label: "Markdown",
      ...buildMarkdownRow({ monthKeys, hybridAgg, summaryPeriods }),
    }));
  }

  const custoRows = [
    {
      key: "custo-unitario", label: "CMV Unitário",
      ...cmv?.buildCmvUnitarioRow({ monthKeys, hybridAgg, cmvAgg, usePriceCostAsCMV }),
    },
  ];

  if (custosPreco) {
    custoRows.push(asRevealed({
      key: "custo-markup-inicial", label: "Markup Inicial",
      ...markup?.buildMarkupInicialRow({ monthKeys, hybridAgg, pecasAgg, reaisAgg, cmvAgg, usePriceCostAsCMV, originalPriceRawValues: precoRows[0].rawValues, summaryPeriods }),
    }));
    custoRows.push(asRevealed({
      key: "custo-markup-final", label: "Markup Final",
      ...markup?.buildMarkupFinalRow({ monthKeys, hybridAgg, cmvAgg, usePriceCostAsCMV, revenueRawValues: faturamentoRows[0].rawValues, summaryPeriods }),
    }));
    custoRows.push(asRevealed({
      key: "custo-cmv-total", label: "CMV Total", bold: true,
      ...cmv?.buildCmvTotalRow({ monthKeys, hybridAgg, cmvAgg, usePriceCostAsCMV }),
    }));
    custoRows.push(asRevealed({
      key: "custo-lucro-bruto", label: "Lucro Bruto", bold: true,
      ...buildGrossProfitRow({ monthKeys, hybridAgg, summaryPeriods }),
    }));
  }

  return { faturamentoRows, vendaPecasRows, precoRows, custoRows };
};